import React from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Chip,
    LinearProgress,
    Grid2,
    Tooltip
} from '@mui/material';
import {
    Pool as PoolIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon,
    AutoMode as AutoModeIcon
} from '@mui/icons-material';
import { PoolHealth } from '../../types/DashboardTypes';

interface PoolHealthCardProps {
    pool: PoolHealth;
    contractAddress?: string;
}

const PoolHealthCard: React.FC<PoolHealthCardProps> = ({
    pool,
    contractAddress
}) => {
    const formatTokenAmount = (amount: string, decimals: number = 18) => {
        const value = parseFloat(amount) / Math.pow(10, decimals);
        if (value >= 1e6) {
            return (value / 1e6).toFixed(2) + 'M';
        } else if (value >= 1e3) {
            return (value / 1e3).toFixed(2) + 'K';
        }
        return value.toFixed(4);
    };

    const formatPercentage = (basisPoints: number) => {
        return (basisPoints / 100).toFixed(2) + '%';
    };

    const getPoolTypeColor = () => {
        return pool.isStable ? 'info' : 'secondary';
    };

    const getAutoCompoundStatus = () => {
        if (!pool.autoCompoundEnabled) {
            return { color: 'default', label: 'Disabled' };
        }

        const timeSinceLastCompound = Date.now() - pool.lastCompoundTime;
        const daysSinceCompound = timeSinceLastCompound / (1000 * 60 * 60 * 24);

        if (daysSinceCompound < 1) {
            return { color: 'success', label: 'Active' };
        } else if (daysSinceCompound < 7) {
            return { color: 'warning', label: 'Pending' };
        } else {
            return { color: 'error', label: 'Stale' };
        }
    };

    const getShareVisualization = () => {
        const sharePercent = pool.sharePercentage / 100; // Convert basis points to percentage
        return Math.min(100, sharePercent);
    };

    const autoCompoundStatus = getAutoCompoundStatus();

    return (
        <Card elevation={2}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <PoolIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            {pool.token0Symbol}-{pool.token1Symbol}
                        </Typography>
                        <Chip
                            label={pool.isStable ? "Stable" : "Volatile"}
                            color={getPoolTypeColor()}
                            size="small"
                        />
                    </Box>
                }
                subheader={
                    <Box>
                        <Typography variant="caption" color="textSecondary">
                            Pool: {pool.pool.slice(0, 10)}...{pool.pool.slice(-8)}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="textSecondary">
                            Last updated: {new Date(pool.lastUpdated).toLocaleTimeString()}
                        </Typography>
                    </Box>
                }
            />
            <CardContent>
                <Grid2 container spacing={2}>
                    {/* Pool Share */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Pool Share
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    {formatPercentage(pool.sharePercentage)}
                                </Typography>
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={getShareVisualization()}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: 'primary.main'
                                    }
                                }}
                            />

                            <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Your LP: {formatTokenAmount(pool.lpBalance)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Total: {formatTokenAmount(pool.lpTotalSupply)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Pool Reserves */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Pool Reserves
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token0Symbol}:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.reserve0)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token1Symbol}:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.reserve1)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Your Position Value */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                💎 Your Position Value
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token0Symbol}:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.estimatedValue0)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token1Symbol}:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.estimatedValue1)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Unclaimed Fees */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1, mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                💰 Unclaimed Fees
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token0Symbol} Fees:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.unclaimedFees0)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                    {pool.token1Symbol} Fees:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatTokenAmount(pool.unclaimedFees1)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Auto-Compound Status */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AutoModeIcon fontSize="small" />
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Auto-Compound
                                </Typography>
                            </Box>
                            <Chip
                                label={autoCompoundStatus.label}
                                color={autoCompoundStatus.color as any}
                                size="small"
                            />
                        </Box>

                        {pool.autoCompoundEnabled && (
                            <Typography variant="caption" color="textSecondary">
                                Last compound: {new Date(pool.lastCompoundTime).toLocaleDateString()}
                            </Typography>
                        )}
                    </Grid2>

                    {/* Pool Insights */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                                💡 {pool.isStable ?
                                    'Stable pools have lower volatility and consistent fees' :
                                    'Volatile pools can generate higher fees during price movements'
                                }
                            </Typography>
                        </Box>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default PoolHealthCard;
